import classNames from 'classnames';
import { useState, useContext, useCallback, useEffect, useRef } from 'react';
import FJProjectStore from '../../store/FJProjectStore';
import { logShortCardInteraction } from '../../util/FJLongToShortEa';
import FJButton, { FJButtonType } from '../../common/components/FJButton/FJButton';
import FJDislikeIcon from '../../assets/dislike.svg?react';
import FJDislikedIcon from '../../assets/disliked.svg?react';
import FJHeartRedIcon from '../../assets/heart-red.svg?react';
import FJHeartIcon from '../../assets/heart.svg?react';
import FJRetryIcon from '../../assets/retry.svg?react';
import FJLocalStore from '../../store/FJLocalStore';
import FJDetailsCardToolbar from './FJDetailsCardToolbar';
import FJDetailsMediaPanel, { type FJDetailsVideoLoadPhase } from './FJDetailsMediaPanel';
import FJDetailsTranscriptList from './FJDetailsTranscriptList';
import FJDetailsViralPanel from './FJDetailsViralPanel';
import styles from './FJDetailsCard.module.less';
import FJToolTip from '@src/common/components/FJToolTip/FJToolTip';
import FJSkeletonScreen from '@src/common/components/FJSkeletonScreen/FJSkeletonScreen';
import type { FJDetailsListItem, FJDetailsListItemSuccess } from '../../types/FJDetails';
import { GENERATE_STATE } from '../../constants/FJEnumConstant';
import { FJDetailsPlaybackContext } from './FJDetailsPlaybackContext';
import FJCheckBox from '../FJCheckBox/FJCheckBox';

function isDetailsSuccessItem(item: FJDetailsListItem): item is FJDetailsListItemSuccess {
    return 'videoData' in item;
}

interface FJDetailsCardProps {
    item: FJDetailsListItem;
    /** 列表中的展示序号（从 1 起，与侧栏一致） */
    ordinal: number;
    isSelected: boolean;
    isClipSelected?: boolean;
    onToggleClipSelected?: (id: string, shiftKey?: boolean) => void;
    onToggleLike: (id: string) => void;
    onToggleDislike: (id: string) => void;
    onTryAgain?: (id: string) => void;
    onPublished?: (clipId: string) => void;
    onPublishSocial?: (clipId: string) => void;
    onExportDownload?: (clipId: string, callback: () => void) => void;
    onTrim?: (clipId: string) => void;
    onCut?: (clipId: string, callback: () => void) => void;
    onMoreRename?: (clipId: string) => void;
    onMoreDelete?: (clipId: string) => void;
    onClearClipSelection?: () => void;
}

const GENERATING_SKELETON_LAYOUT = [
    [
        { width: 222, height: 380 },
        { width: 686, height: 108 },
        { width: 686, height: 254 },
    ],
];

export default function FJDetailsCard({
    item,
    ordinal,
    isSelected,
    isClipSelected = false,
    onToggleClipSelected,
    onToggleLike,
    onToggleDislike,
    onTryAgain,
    onPublished,
    onPublishSocial,
    onExportDownload,
    onTrim,
    onCut,
    onMoreRename,
    onMoreDelete,
    onClearClipSelection,
}: FJDetailsCardProps) {
    const detailsPlayback = useContext(FJDetailsPlaybackContext);
    const previewPauseRef = useRef<() => void>(() => {});
    /** `view` 每条短片在成功详情卡挂载时只报一次；勿把整份 `item` 放进依赖（点赞等会换新引用导致重复上报） */
    const shortViewReportedRef = useRef(false);

    useEffect(() => {
        shortViewReportedRef.current = false;
    }, [item.id]);

    useEffect(() => {
        if (item.status !== GENERATE_STATE.SUCCEEDED || !isDetailsSuccessItem(item) || item.disliked) {
            return;
        }
        if (shortViewReportedRef.current) {
            return;
        }
        shortViewReportedRef.current = true;
        const eid = FJProjectStore.getEventId() ?? '';
        logShortCardInteraction({
            action_type: 'view',
            project_id: eid,
            short_id: item.id,
            position: ordinal,
            viral_score: item.score,
            video_duration: item.end - item.start,
        });
        // 仅 clip 与成败态变化时重挂；`item.disliked` 用于从折叠态展开为完整卡时再尝试一次 view
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.id, item.status]);

    /** 先直接 pause 当前预览（仅 clearPlayback 无法停「本卡正在播」），再释放独占播放权 */
    const pausePreviewPlayback = useCallback(() => {
        previewPauseRef.current();
        detailsPlayback?.clearPlayback();
    }, [detailsPlayback]);

    const exitClipSelection = useCallback(() => {
        onClearClipSelection?.();
    }, [onClearClipSelection]);

    const onPausePreviewReady = useCallback((pause: () => void) => {
        previewPauseRef.current = pause;
    }, []);

    const indexLabel = String(ordinal).padStart(2, '0');
    const [summaryExpanded, setSummaryExpanded] = useState(false);
    /** 仅 `ready` 时允许点赞/工具条等；加载中与加载失败（含 FJRetry）时禁用 */
    const [videoLoadPhase, setVideoLoadPhase] = useState<FJDetailsVideoLoadPhase>('loading');
    const videoActionsLocked = videoLoadPhase !== 'ready';

    const onVideoLoadPhaseChange = useCallback((phase: FJDetailsVideoLoadPhase) => {
        setVideoLoadPhase(phase);
    }, []);

    useEffect(() => {
        setVideoLoadPhase('loading');
    }, [item.id]);

    useEffect(() => {
        previewPauseRef.current = () => {};
    }, [item.id]);

    useEffect(() => {
        if (
            !detailsPlayback ||
            item.status !== GENERATE_STATE.SUCCEEDED ||
            !isDetailsSuccessItem(item) ||
            item.disliked
        ) {
            return;
        }
        return detailsPlayback.registerPauseHandler(item.id, () => {
            previewPauseRef.current();
        });
    }, [detailsPlayback, item.id, item.status, item.disliked]);

    if (item.status === GENERATE_STATE.PROCESSING || item.status === GENERATE_STATE.PENDING) {
        return (
            <article
                className={classNames(styles.root, styles.root_success, isSelected && styles.root_selected)}
                data-details-id={item.id}
            >
                <FJSkeletonScreen list={GENERATING_SKELETON_LAYOUT} />
            </article>
        );
    }

    if (item.status === GENERATE_STATE.FAILED) {
        return (
            <article
                className={classNames(styles.root, styles.root_failed, isSelected && styles.root_selected)}
                data-details-id={item.id}
            >
                <div className={styles.failed_outer_index}>
                    <FJCheckBox
                        selected={isClipSelected}
                        dataChange={(_, e) => onToggleClipSelected?.(item.id, e.shiftKey)}
                    />
                    {indexLabel}
                </div>
                <div className={styles.failed_panel}>
                    <p className={styles.failed_text}>{FJLocalStore._('failed-to-generate-this-content')}</p>
                    <FJToolTip title={FJLocalStore._('try-again')}>
                        <FJButton
                            type={FJButtonType.GRADIENT}
                            className={styles.failed_try_btn}
                            textClassName={styles.failed_try_btn_text}
                            icon={<FJRetryIcon aria-hidden style={{ marginRight: 8 }} />}
                            text='try-again'
                            onClick={() => {
                                exitClipSelection();
                                onTryAgain?.(item.id);
                            }}
                        />
                    </FJToolTip>
                </div>
            </article>
        );
    }

    if (!isDetailsSuccessItem(item)) {
        return null;
    }

    if (item.disliked) {
        return (
            <article
                className={classNames(
                    styles.root,
                    styles.root_success,
                    styles.root_collapsed_disliked,
                    isSelected && styles.root_selected,
                )}
                data-details-id={item.id}
            >
                <div className={styles.collapsed_row}>
                    <FJCheckBox
                        selected={isClipSelected}
                        dataChange={(_, e) => onToggleClipSelected?.(item.id, e.shiftKey)}
                    />
                    <button
                        type='button'
                        className={styles.collapsed_dislike_btn}
                        aria-pressed
                        aria-label={'remove dislike'}
                        onClick={e => {
                            exitClipSelection();
                            pausePreviewPlayback();
                            onToggleDislike(item.id);
                            e.currentTarget.blur();
                        }}
                    >
                        <FJDislikedIcon className={styles.collapsed_dislike_svg} aria-hidden />
                    </button>
                    <div className={styles.collapsed_title_box}>
                        <span className={styles.collapsed_index}>#{indexLabel}</span>
                        <p className={styles.collapsed_title_one_line}>{item.title}</p>
                    </div>
                </div>
            </article>
        );
    }

    return (
        <article
            className={classNames(styles.root, styles.root_success, isSelected && styles.root_selected)}
            data-details-id={item.id}
        >
            <header className={styles.card_header}>
                <div className={styles.heading}>
                    <span className={styles.index_badge}>
                        <FJCheckBox
                        selected={isClipSelected}
                        dataChange={(_, e) => onToggleClipSelected?.(item.id, e.shiftKey)}
                    />
                        {indexLabel}
                    </span>
                    <h2 className={styles.card_title}>{item.title}</h2>
                </div>
                <div
                    className={classNames(videoActionsLocked && styles.actions_locked)}
                    aria-disabled={videoActionsLocked}
                >
                    <FJDetailsCardToolbar
                        clipId={item.id}
                        publishedList={item.publishedList ?? []}
                        onPublished={() => {
                            exitClipSelection();
                            pausePreviewPlayback();
                            onPublished?.(item.id);
                        }}
                        onPublishSocial={() => {
                            exitClipSelection();
                            pausePreviewPlayback();
                            onPublishSocial?.(item.id);
                        }}
                        onDownload={(callback: () => void) => {
                            exitClipSelection();
                            pausePreviewPlayback();
                            onExportDownload?.(item.id, callback);
                        }}
                        onTrim={() => {
                            exitClipSelection();
                            pausePreviewPlayback();
                            onTrim?.(item.id);
                        }}
                        onCut={(callback: () => void) => {
                            exitClipSelection();
                            pausePreviewPlayback();
                            onCut?.(item.id, callback);
                        }}
                        onMoreRename={id => {
                            exitClipSelection();
                            pausePreviewPlayback();
                            onMoreRename?.(id);
                        }}
                        onMoreDelete={id => {
                            exitClipSelection();
                            pausePreviewPlayback();
                            onMoreDelete?.(id);
                        }}
                    />
                </div>
            </header>

            <div className={styles.body_panel_row}>
                <div
                    className={classNames(styles.reactions_rail, videoActionsLocked && styles.actions_locked)}
                    aria-disabled={videoActionsLocked}
                >
                    <FJToolTip title={FJLocalStore._('like')}>
                        <button
                            type='button'
                            className={styles.react_btn}
                            aria-pressed={item.liked}
                            aria-label={FJLocalStore._('like')}
                            onClick={e => {
                                exitClipSelection();
                                pausePreviewPlayback();
                                onToggleLike(item.id);
                                e.currentTarget.blur();
                            }}
                        >
                            <span key={item.liked ? 'liked' : 'unliked'} className={styles.reaction_icon_flash}>
                                {item.liked ? (
                                    <FJHeartRedIcon className={styles.reaction_svg} aria-hidden />
                                ) : (
                                    <FJHeartIcon className={styles.reaction_svg} aria-hidden />
                                )}
                            </span>
                        </button>
                    </FJToolTip>
                    <FJToolTip title={FJLocalStore._('dislike')}>
                        <button
                            type='button'
                            className={styles.react_btn}
                            aria-pressed={!!item.disliked}
                            aria-label={FJLocalStore._('dislike')}
                            onClick={e => {
                                exitClipSelection();
                                pausePreviewPlayback();
                                onToggleDislike(item.id);
                                e.currentTarget.blur();
                            }}
                        >
                            <span
                                key={item.disliked ? 'disliked' : 'undisliked'}
                                className={styles.reaction_icon_flash}
                            >
                                {item.disliked ? (
                                    <FJDislikedIcon className={styles.reaction_svg} aria-hidden />
                                ) : (
                                    <FJDislikeIcon className={styles.reaction_svg} aria-hidden />
                                )}
                            </span>
                        </button>
                    </FJToolTip>
                </div>
                <div className={styles.body_panel}>
                    <div className={styles.body}>
                        <FJDetailsMediaPanel
                            detailId={item.id}
                            videoData={item.videoData}
                            exportRatio={item.exportRatio}
                            fillHeight
                            onVideoLoadPhaseChange={onVideoLoadPhaseChange}
                            onPausePreviewReady={onPausePreviewReady}
                            onPlayStart={exitClipSelection}
                            shortCardEa={{
                                project_id: FJProjectStore.getEventId() ?? '',
                                short_id: item.id,
                                position: ordinal,
                                viral_score: item.score,
                                video_duration: item.end - item.start,
                            }}
                        />
                        <div className={styles.col_info}>
                            <FJDetailsViralPanel
                                score={item.score}
                                summary={item.summary}
                                summaryExpanded={summaryExpanded}
                                onToggleSummary={() => {
                                    setSummaryExpanded(v => {
                                        const next = !v;
                                        if (!v && next) {
                                            const eid = FJProjectStore.getEventId() ?? '';
                                            logShortCardInteraction({
                                                action_type: 'expand_detail',
                                                project_id: eid,
                                                short_id: item.id,
                                                position: ordinal,
                                                viral_score: item.score,
                                                video_duration: item.end - item.start,
                                            });
                                        }
                                        return next;
                                    });
                                }}
                            />
                            <FJDetailsTranscriptList subtitleList={item.subtitleList} />
                        </div>
                    </div>
                </div>
            </div>
        </article>
    );
}
